import { datadogRum } from '@datadog/browser-rum';
import type { RumInitConfiguration } from '@datadog/browser-rum';

// Datadog RUM configuration interface
interface DatadogConfig {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: string;
  version: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  trackUserInteractions: boolean;
  trackResources: boolean;
  trackLongTasks: boolean;
  defaultPrivacyLevel: 'allow' | 'mask' | 'mask-user-input';
}

// Get configuration from environment variables
const getDatadogConfig = (): DatadogConfig | null => {
  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
  const site = process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.com';
  const service = process.env.NEXT_PUBLIC_DATADOG_SERVICE_NAME || 'llm-docs-app';
  const env = process.env.NEXT_PUBLIC_DATADOG_ENV || 'development';
  const version = process.env.NEXT_PUBLIC_DATADOG_VERSION || '1.0.0';

  // Return null if required environment variables are missing
  if (!applicationId || !clientToken) {
    console.warn('Datadog RUM: Missing required environment variables (applicationId or clientToken)');
    return null;
  }

  return {
    applicationId,
    clientToken,
    site,
    service,
    env,
    version,
    sessionSampleRate: env === 'production' ? 10 : env === 'staging' ? 50 : 100, // 10% prod, 50% staging, 100% dev
    sessionReplaySampleRate: env === 'production' ? 5 : env === 'staging' ? 15 : 30, // 5% prod, 15% staging, 30% dev
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input' as const,
  };
};

// Initialize Datadog RUM
export const initializeDatadogRUM = (): boolean => {
  // Only initialize on client side
  if (typeof window === 'undefined') {
    return false;
  }

  const config = getDatadogConfig();
  if (!config) {
    return false;
  }

  try {
    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
              site: config.site as RumInitConfiguration['site'],
      service: config.service,
      env: config.env,
      version: config.version,
      sessionSampleRate: config.sessionSampleRate,
      sessionReplaySampleRate: config.sessionReplaySampleRate,
      trackUserInteractions: config.trackUserInteractions,
      trackResources: config.trackResources,
      trackLongTasks: config.trackLongTasks,
      defaultPrivacyLevel: config.defaultPrivacyLevel,
      // Enable React Router integration
      enableExperimentalFeatures: ['clickmap'],
    });

    console.log(`Datadog RUM initialized for ${config.env} environment`);
    return true;
  } catch (error) {
    console.error('Failed to initialize Datadog RUM:', error);
    return false;
  }
};

// Utility functions for custom tracking
export const trackCustomEvent = (name: string, context?: Record<string, any>) => {
  if (typeof window !== 'undefined' && datadogRum.getInitConfiguration()) {
    datadogRum.addAction(name, context);
  }
};

export const trackError = (error: Error, context?: Record<string, any>) => {
  if (typeof window !== 'undefined' && datadogRum.getInitConfiguration()) {
    datadogRum.addError(error, context);
  }
};

export const setUserContext = (user: { id?: string; name?: string; email?: string }) => {
  if (typeof window !== 'undefined' && datadogRum.getInitConfiguration()) {
    datadogRum.setUser(user);
  }
};

export const setGlobalContext = (context: Record<string, any>) => {
  if (typeof window !== 'undefined' && datadogRum.getInitConfiguration()) {
    datadogRum.setGlobalContextProperty('custom', context);
  }
}; 